import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from '@xyflow/react';
import { Panel } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './BuildFlow/xy-theme.css';
import AnnotationNode from './BuildFlow/AnnotationNode';
import ToolbarNode from './BuildFlow/ToolbarNode';
import CircleNode from './BuildFlow/CircleNode';
import ButtonEdge from './BuildFlow/ButtonEdge';
import DiamondNode from './BuildFlow/DiamondNode';
import DescriptionNode from './BuildFlow/DescriptionNode';
import URLNode from './BuildFlow/URLNode';
import DataNode from './BuildFlow/DataNode';
import StoredDataNode from './BuildFlow/StoredDataNode';
import PreparationNode from './BuildFlow/PreparationNode';
import TerminalNode from './BuildFlow/TerminalNode';
import { fetchGraph, saveGraph } from './Flow';

export default function FlowEditor({
  flowchartId,
  name, // controlled
  description, // controlled
  onMetaChange, // (patch) => void
  onMetaLoaded, // ({name, description}) => void
}) {
  const connectStart = useRef(null);
  const handleConnectStart = useCallback((_e, params) => {
    // params: { nodeId, handleId, handleType: 'source' | 'target' }
    connectStart.current = params;
  }, []);

  const handleConnectEnd = useCallback(() => {
    connectStart.current = null;
  }, []);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(true);

  const DEFAULT_EDGE_COLOR = '#b1b1b7';
  const DEFAULT_EDGE_WIDTH = 1;

  // Reset local state when switching flowchart
  useEffect(() => {
    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
    setError(null);

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const graph = await fetchGraph(flowchartId);

        const nextNodes = graph.nodes.map((n) => ({
          id: n.id,
          type: n.type || 'default',
          position: n.position,
          data: {
            label: n.data?.label ?? 'Node',
            description: n.data?.description ?? '',
            ...n.data,
          },
          style: {
            ...(n.data?.width != null ? { width: n.data.width } : {}),
            ...(n.data?.height != null ? { height: n.data.height } : {}),
          },
          draggable: true,
        }));
        const toMarker = (m) => m || undefined;

        const nextEdges = graph.edges.map((e) => {
          const d = e.data ?? {};
          return {
            id: e.id,
            source: e.source,
            target: e.target,

            // restore handles so the edge attaches to the correct side/handle
            sourceHandle: e.sourceHandle ?? d.sourceHandle ?? undefined,
            targetHandle: e.targetHandle ?? d.targetHandle ?? undefined,

            label: e.label ?? '',
            type: e.type ?? d.edgeType ?? undefined,
            animated: e.animated ?? !!d.edgeAnimated,
            style: e.style ?? d.edgeStyle ?? undefined,
            className: e.className ?? d.edgeClassName ?? undefined,
            labelStyle: e.labelStyle ?? d.edgeLabelStyle ?? undefined,
            markerStart: toMarker(e.markerStart ?? d.edgeMarkerStart),
            markerEnd: toMarker(e.markerEnd ?? d.edgeMarkerEnd),

            data: d, // keep whatever else you stored
          };
        });

        if (!cancelled) {
          setNodes(nextNodes);
          setEdges(nextEdges);
          onMetaLoaded?.({
            name: graph.name ?? '',
            description: graph.description ?? '',
          });
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load graph');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [flowchartId, setNodes, setEdges]);

  function normalizeByStart(params, startedOnTarget) {
    if (!startedOnTarget) return params;

    return {
      ...params,
      source: params.target,
      sourceHandle: params.targetHandle ?? undefined, // same id, opposite type
      target: params.source,
      targetHandle: params.sourceHandle ?? undefined, // same id, opposite type
    };
  }

  const onConnect = useCallback(
    (raw) => {
      const startedOnTarget = connectStart.current?.handleType === 'target';
      const newEdgeId = crypto.randomUUID(); // Generate a valid UUID for the new edge
      const params = normalizeByStart(raw, startedOnTarget);

      setEdges((eds) =>
        addEdge(
          {
            ...params,
            id: newEdgeId, // The generated UUID for the edge
            label: '',
            data: {
              sourceHandle: params.sourceHandle ?? null,
              targetHandle: params.targetHandle ?? null,
              markerEnd: { type: MarkerType.ArrowClosed },
            },
            
             
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId]
  );

  const DEFAULT_NODE_TYPE = 'fnnNode';

  const addNode = useCallback(() => {
    const id = crypto.randomUUID();
    const newNode = {
      id,
      position: { x: 120 + Math.random() * 360, y: 120 + Math.random() * 220 },
      data: { label: 'New block', description: '' },
      type: DEFAULT_NODE_TYPE,
    };
    setNodes((ns) => [...ns, newNode]);
    setSelectedNodeId(id);
  }, [setNodes]);

  const removeSelected = useCallback(() => {
    if (!selectedNodeId && !selectedEdgeId) return;
    if (selectedNodeId) {
      setNodes((ns) => ns.filter((n) => n.id !== selectedNodeId));
      setEdges((es) =>
        es.filter(
          (e) => e.source !== selectedNodeId && e.target !== selectedNodeId
        )
      );
      setSelectedNodeId(null);
    }
    if (selectedEdgeId) {
      setEdges((es) => es.filter((e) => e.id !== selectedEdgeId));
      setSelectedEdgeId(null);
    }
  }, [selectedNodeId, selectedEdgeId, setNodes, setEdges]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        replace: true,
        nodes: nodes.map((n) => ({
          id: n.id,
          type: n.type,
          position: n.position,
          data: { ...n.data, description: n.data.description || '' },
        })),
        edges: edges.map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          label: typeof e.label === 'string' ? e.label : undefined,
          data: {
            ...e.data,
            edgeType: e.type ?? null,
            edgeAnimated: !!e.animated,
            edgeStyle: e.style ?? null,
            edgeClassName: e.className ?? null,
            edgeLabelStyle: e.labelStyle ?? null,
            edgeMarkerStart: e.markerStart ?? null,
            edgeMarkerEnd: e.markerEnd ?? null,
            sourceHandle: e.sourceHandle ?? null,
            targetHandle: e.targetHandle ?? null,
          },
        })),
      };
      await saveGraph(flowchartId, name, description, payload);
    } catch (e) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [flowchartId, nodes, edges, name, description]);

  const updateSelectedEdge = useCallback(
    (patch) => {
      setEdges((es) =>
        es.map((e) => (e.id === selectedEdgeId ? { ...e, ...patch } : e))
      );
    },
    [selectedEdgeId, setEdges]
  );

  const updateSelectedEdgeStyle = useCallback(
    (patch) => {
      setEdges((es) =>
        es.map((e) =>
          e.id === selectedEdgeId
            ? { ...e, style: { ...(e.style ?? {}), ...patch } }
            : e
        )
      );
    },
    [selectedEdgeId, setEdges]
  );

  const updateSelectedEdgeColor = useCallback(
    (color) => {
      setEdges((es) =>
        es.map((e) =>
          e.id === selectedEdgeId
            ? {
                ...e,
                style: { ...(e.style ?? {}), stroke: color },
                markerEnd: e.markerEnd
                  ? { ...e.markerEnd, color }
                  : { type: MarkerType.ArrowClosed, color },
              }
            : e
        )
      );
    },
    [selectedEdgeId, setEdges]
  );

  const updateSelectedData = useCallback(
    (patch) => {
      if (!selectedNodeId) return;
      setNodes((ns) =>
        ns.map((n) =>
          n.id === selectedNodeId ? { ...n, data: { ...n.data, ...patch } } : n
        )
      );
    },
    [selectedNodeId, setNodes]
  );

  const updateSelectedType = useCallback(
    (newType) => {
      if (!selectedNodeId) return;
      setNodes((ns) =>
        ns.map((n) => (n.id === selectedNodeId ? { ...n, type: newType } : n))
      );
    },
    [selectedNodeId, setNodes]
  );

  if (loading) return <div className="p-6">Loading…</div>;

  const nodeTypes = {
    fnnNode: (props) => <DescriptionNode {...props} />,
    annotation: (props) => <AnnotationNode {...props} />,
    circle: (props) => <CircleNode {...props} />,
    toolbar: (props) => <ToolbarNode {...props} />,
    diamond: (props) => <DiamondNode {...props} />,
    url: (props) => <URLNode {...props} />,
    terminal: (props) => <TerminalNode {...props}/>,
    data: (props) => <DataNode {...props}/>,
    storedData: (props) => <StoredDataNode {...props}/>,
    preparation: (props) => <PreparationNode {...props}/>,
  };

  const edgeTypes = {
    button: ButtonEdge,
  };

  const NODE_TYPE_OPTIONS = [
    { value: 'fnnNode', label: 'Process' },
    { value: 'annotation', label: 'Annotation' },
    
    { value: 'terminal', label: 'Terminal' },
    { value: 'data', label: 'Data' },
    { value: 'storedData', label: 'Stored Data' },
    { value: 'preparation', label: 'Preparation' },


    { value: 'toolbar', label: 'Tag' },
    { value: 'circle', label: 'Connector' },
    { value: 'diamond', label: 'Condition' },
    { value: 'url', label: 'URL' },
  ];

  const SWATCHES = [
    '#111827',
    '#374151',
    '#6b7280',
    '#ffffff',
    '#1d4ed8',
    '#3b82f6',
    '#06b6d4',
    '#10b981',
    '#22c55e',
    '#84cc16',
    '#f59e0b',
    '#ef4444',
    '#f43f5e',
    '#a855f7',
    '#7c3aed',
    'transparent',
  ];
  return (
    <div
      className="w-screen grid grid-cols-[320px_1fr]"
      style={{ height: '80vh' }}
    >
      {/* Sidebar */}
      <div className="pointer-events-none absolute inset-y-15 left-0  z-50 p-3">
        <div
          className={[
            'pointer-events-auto w-[360px] max-w-[92vw] rounded-2xl border bg-white/95 shadow-xl',
            'transition-transform duration-200 ease-out bg-fnn-dark',
            inspectorOpen ? 'translate-x-0' : '-translate-x-[calc(100%+12px)]', // Slide out to the left
            'flex flex-col max-h-[90vh]',
          ].join(' ')}
          role="dialog"
          aria-hidden={!inspectorOpen}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b px-3 py-2">
            <div className="text-sm font-medium">Inspector</div>
            <button
              onClick={() => setInspectorOpen(false)}
              className="rounded-md border px-2 py-1 text-xs hover:bg-gray-50"
            >
              Close
            </button>
          </div>

          {/* Body (scrollable) */}
          <div className="flex-1 overflow-auto p-3 space-y-4">
            {/* Meta (Name/Description) */}
            <div className="space-y-2">
              <label className="block text-xs font-medium">Name</label>
              <input
                className="w-full rounded-lg border px-2 py-1 text-black"
                value={name}
                onChange={(e) => onMetaChange?.({ name: e.target.value })}
                placeholder="Flowchart Name"
              />
              <label className="block text-xs font-medium">Description</label>
              <textarea
                className="w-full rounded-lg border px-2 py-1 text-black"
                value={description}
                onChange={(e) =>
                  onMetaChange?.({ description: e.target.value })
                }
                placeholder="Flowchart Description"
              />
            </div>

            {/* Buttons */}
            <div className="grid items-center gap-2">
              <button
                onClick={addNode}
                className="px-3 py-2 rounded-xl border hover:bg-blue-fnn"
              >
                + Add node
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-2 rounded-xl border disabled:opacity-60 hover:bg-blue-fnn"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>

            {/* Error */}
            {!!error && (
              <div className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Tip */}
            <div className="text-xs text-gray-500">
              Tip: drag to move, connect nodes by dragging from handles.
            </div>

            {/* Inspector: Node/Edge editors (your existing logic) */}
            <div>
              <h3 className="font-semibold mb-2">Inspector</h3>

              {selectedNodeId && selectedNode && (
                <div className="space-y-3">
                  <div className="text-xs text-gray-500">
                    Node ID: {selectedNodeId}
                  </div>

                  {/* Type */}
                  <label className="block text-sm">
                    Type
                    <select
                      className="mt-1 w-full border rounded-lg px-2 py-1 text-black"
                      value={selectedNode.type ?? 'fnnNode'}
                      onChange={(e) => updateSelectedType(e.target.value)}
                    >
                      {NODE_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Label */}
                  <label className="block text-sm">
                    Label
                    <input
                      className="mt-1 w-full border rounded-lg px-2 py-1 text-black"
                      value={selectedNode.data?.label ?? ''}
                      onChange={(e) =>
                        updateSelectedData({ label: e.target.value })
                      }
                    />
                  </label>

                  {/* Description */}
                  {selectedNode.type != 'url' && (
                    <label className="block text-sm">
                      Description
                      <textarea
                        className="mt-1 w-full border rounded-lg px-2 py-1 text-black"
                        value={selectedNode.data?.description ?? ''}
                        onChange={(e) =>
                          updateSelectedData({ description: e.target.value })
                        }
                      />
                    </label>
                  )}

                  {/* URL */}
                  {selectedNode.type == 'url' && (
                    <label className="block text-sm">
                      URL
                      <input
                        className="mt-1 w-full border rounded-lg px-2 py-1 text-black"
                        value={selectedNode.data?.url ?? ''}
                        onChange={(e) =>
                          updateSelectedData({ url: e.target.value })
                        }
                      />
                    </label>
                  )}

                  {/* Fill color */}
                  {selectedNode.type != 'annotation' && (
                    <div className="block text-sm">
                      <div className="mb-1">Fill color</div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          className="h-9 w-12 rounded border"
                          value={selectedNode.data?.bgColor ?? '#ffffff'}
                          onChange={(e) =>
                            updateSelectedData({ bgColor: e.target.value })
                          }
                        />
                        <div className="flex flex-wrap gap-1">
                          {SWATCHES.map((c) => (
                            <button
                              key={c}
                              type="button"
                              aria-label={`Set ${c}`}
                              className="h-6 w-6 rounded border"
                              style={{ background: c }}
                              onClick={() => updateSelectedData({ bgColor: c })}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Font color & size */}
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-sm">
                      Font color
                      <input
                        type="color"
                        className="mt-1 h-9 w-full rounded border"
                        value={selectedNode.data?.fontColor ?? '#111111'}
                        onChange={(e) =>
                          updateSelectedData({ fontColor: e.target.value })
                        }
                      />
                    </label>

                    <label className="block text-sm">
                      Font size (px)
                      <input
                        type="number"
                        min={10}
                        max={48}
                        step={1}
                        className="mt-1 w-full border rounded-lg px-2 py-1 text-black"
                        value={selectedNode.data?.fontSize ?? 14}
                        onChange={(e) =>
                          updateSelectedData({
                            fontSize: Number(e.target.value || 14),
                          })
                        }
                      />
                    </label>
                  </div>

                  <button
                    onClick={removeSelected}
                    className="px-3 py-2 rounded-xl border w-full"
                  >
                    Delete node
                  </button>
                </div>
              )}

              {!selectedNodeId && selectedEdgeId && (
                <div className="space-y-2">
                  <div className="text-xs text-gray-500">
                    Edge ID: {selectedEdgeId}
                  </div>
                  {/* Label */}
                  <label className="block text-sm">
                    Label
                    <input
                      className="mt-1 w-full border rounded-lg px-2 py-1 text-black"
                      value={
                        edges.find((e) => e.id === selectedEdgeId)?.label ?? ''
                      }
                      onChange={(e) =>
                        updateSelectedEdge({
                          label: e.target.value || undefined,
                        })
                      }
                    />
                  </label>

                  {/* Animated */}
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={
                        !!edges.find((e) => e.id === selectedEdgeId)?.animated
                      }
                      onChange={(e) => {
                        const on = e.target.checked;
                        // built-in edges respect `animated`; custom edges can also get a class for CSS
                        setEdges((es) =>
                          es.map((edge) =>
                            edge.id === selectedEdgeId
                              ? {
                                  ...edge,
                                  animated: on,
                                  className: on
                                    ? [edge.className, 'animated-edge']
                                        .filter(Boolean)
                                        .join(' ')
                                    : (edge.className || '')
                                        .replace(/\banimated-edge\b/, '')
                                        .trim() || undefined,
                                }
                              : edge
                          )
                        );
                      }}
                    />
                    Animated
                  </label>

                  {/* Color */}
                  <label className="block text-sm">
                    Stroke color
                    <input
                      type="color"
                      className="mt-1 h-9 w-full rounded border"
                      value={
                        edges.find((e) => e.id === selectedEdgeId)?.style
                          ?.stroke ?? DEFAULT_EDGE_COLOR
                      }
                      onChange={(e) => updateSelectedEdgeColor(e.target.value)}
                    />
                  </label>

                  {/* Thickness */}
                  <label className="block text-sm">
                    Stroke width
                    <input
                      type="number"
                      min={1}
                      max={12}
                      step={1}
                      className="mt-1 w-full border rounded-lg px-2 py-1 text-black"
                      value={
                        edges.find((e) => e.id === selectedEdgeId)?.style
                          ?.strokeWidth ?? DEFAULT_EDGE_WIDTH
                      }
                      onChange={(e) =>
                        updateSelectedEdgeStyle({
                          strokeWidth: Number(
                            e.target.value || DEFAULT_EDGE_WIDTH
                          ),
                        })
                      }
                    />
                  </label>

                  {/* (Optional) label color */}
                  <label className="block text-sm">
                    Label color
                    <input
                      type="color"
                      className="mt-1 h-9 w-full rounded border"
                      value={
                        edges.find((e) => e.id === selectedEdgeId)?.labelStyle
                          ?.fill ?? '#333333'
                      }
                      onChange={(e) =>
                        updateSelectedEdge({
                          labelStyle: { fill: e.target.value },
                        })
                      }
                    />
                  </label>

                  <button
                    onClick={removeSelected}
                    className="px-3 py-2 rounded-xl border w-full"
                  >
                    Delete edge
                  </button>
                </div>
              )}

              {!selectedNodeId && !selectedEdgeId && (
                <div className="text-sm text-gray-600">
                  Select a node or edge to edit.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="relative" style={{ width: '98vw' }}>
        <ReactFlow
          nodes={nodes}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnectStart={handleConnectStart}
          onConnectEnd={handleConnectEnd}
          onConnect={onConnect}
          onNodeClick={(_, n) => {
            setSelectedNodeId(n.id);
            setSelectedEdgeId(null);
          }}
          onEdgeClick={(_, e) => {
            setSelectedEdgeId(e.id);
            setSelectedNodeId(null);
          }}
          onSelectionChange={({ nodes: selNodes, edges: selEdges }) => {
            setSelectedNodeId(selNodes?.[0]?.id ?? null);
            setSelectedEdgeId(selEdges?.[0]?.id ?? null);
          }}
          deleteKeyCode={null} 
          fitView
        >
          <MiniMap />
          <Controls />
          <Background />
          <Panel position="top-right">
            <button
              onClick={() => setInspectorOpen((v) => !v)}
              className="px-3 py-1.5 rounded-lg border bg-gray/90 backdrop-blur shadow-sm hover:shadow"
              title="Toggle Inspector"
            >
              {inspectorOpen ? 'Hide Inspector' : 'Show Inspector'}
            </button>
          </Panel>
        </ReactFlow>
      </div>
    </div>
  );
}
